import { t } from 'i18next';
import { useAtom } from 'jotai';
import _ from 'lodash';
import { ChangeEvent, useMemo } from 'react';

import { Option } from '@/lib/v2/components';

import { geolocation } from '@/src/ContactsModule/utils/Geolocation';
import {
  atomConditionsValues,
  ConditionState,
} from '@/src/modules/AutomationModuleNew/atoms/flows';
import {
  BELONG,
  CONDITIONS_SELECT,
  CUSTOM_FIELD,
  NOT_BELONG,
  TYPE_GROUPS,
  TYPE_TAGS,
} from '@/src/modules/AutomationModuleNew/constants/flows';

import { Field } from '@/modules/ContactsModule/types/Segments';

export const selectTypesOptions: Option[] = [
  {
    id: CUSTOM_FIELD,
    name: t('useBinaryConditional.customFields') as unknown as string,
    value: CUSTOM_FIELD,
  },
  {
    id: TYPE_GROUPS,
    name: t('useBinaryConditional.group') as unknown as string,
    value: TYPE_GROUPS,
  },
  {
    id: TYPE_TAGS,
    name: t('useBinaryConditional.tag') as unknown as string,
    value: TYPE_TAGS,
  },
];

export const selectBelongOptions: Option[] = [
  {
    id: BELONG,
    name: t('useBinaryConditional.belongs') as unknown as string,
    value: BELONG,
  },
  {
    id: NOT_BELONG,
    name: t('useBinaryConditional.notBelong') as unknown as string,
    value: NOT_BELONG,
  },
];

export const selectCFUniqueOptions: Option[] = [
  {
    id: CONDITIONS_SELECT.distinct,
    name: t('useBinaryConditional.distinct') as unknown as string,
    value: CONDITIONS_SELECT.distinct,
  },
  {
    id: CONDITIONS_SELECT.equals,
    name: t('useBinaryConditional.equals') as unknown as string,
    value: CONDITIONS_SELECT.equals,
  },
];

export const selectCFNumberOptions: Option[] = [
  {
    id: CONDITIONS_SELECT.equals,
    name: t('useBinaryConditional.equals') as unknown as string,
    value: CONDITIONS_SELECT.equals,
  },
  {
    id: CONDITIONS_SELECT.greaterThanOrEquals,
    name: t('useBinaryConditional.greaterThanOrEquals') as unknown as string,
    value: CONDITIONS_SELECT.greaterThanOrEquals,
  },
  {
    id: CONDITIONS_SELECT.lessThanOrEquals,
    name: t('useBinaryConditional.lessThanOrEquals') as unknown as string,
    value: CONDITIONS_SELECT.lessThanOrEquals,
  },
  {
    id: CONDITIONS_SELECT.otherThan,
    name: t('useBinaryConditional.otherThan') as unknown as string,
    value: CONDITIONS_SELECT.otherThan,
  },
];

export const selectCFMultipleOptions: Option[] = [
  {
    id: CONDITIONS_SELECT.contains,
    name: t('useBinaryConditional.contains') as unknown as string,
    value: CONDITIONS_SELECT.contains,
  },
  {
    id: CONDITIONS_SELECT.notContains,
    name: t('useBinaryConditional.notContains') as unknown as string,
    value: CONDITIONS_SELECT.notContains,
  },
  {
    id: CONDITIONS_SELECT.startsWith,
    name: t('useBinaryConditional.startsWith') as unknown as string,
    value: CONDITIONS_SELECT.startsWith,
  },
  {
    id: CONDITIONS_SELECT.endsWith,
    name: t('useBinaryConditional.endsWith') as unknown as string,
    value: CONDITIONS_SELECT.endsWith,
  },
  {
    id: CONDITIONS_SELECT.equals,
    name: t('useBinaryConditional.equals') as unknown as string,
    value: CONDITIONS_SELECT.equals,
  },
];

export const genderOptions: Option[] = [
  { id: 'M', name: t('SEGMENTS_CONDITIONS.female') as unknown as string, value: 'M' },
  { id: 'H', name: t('SEGMENTS_CONDITIONS.male') as unknown as string, value: 'H' },
];

export const countryOptions: Option[] = Object.entries(geolocation).map(([id, name]) => ({
  id,
  name,
}));

export const getSelectConditionsOptions = (
  selectTypeValue: ConditionState['selectTypeValue'],
  selectCustomFieldsValue: ConditionState['selectCustomFieldsValue']
) => {
  if (selectTypeValue?.value === CUSTOM_FIELD) {
    if (!selectCustomFieldsValue) return selectCFMultipleOptions;
    if (selectCustomFieldsValue?.data.type === 'number') {
      return selectCFNumberOptions;
    }
    if (selectCustomFieldsValue?.data.metadata.optionType) {
      return selectCFUniqueOptions;
    }

    return selectCFMultipleOptions;
  }
  return selectBelongOptions;
};

export const useBinaryConditional = (index: number = 0) => {
  const [conditionsValues, setConditionsValues] = useAtom(atomConditionsValues);

  const selectConditionOptions = useMemo<Option[]>(() => {
    const currentConditionState = conditionsValues[index];
    return getSelectConditionsOptions(
      currentConditionState.selectTypeValue,
      currentConditionState.selectCustomFieldsValue
    );
  }, [conditionsValues, index]);

  const optionsCustomFields = useMemo<Option[]>(() => {
    const currentConditionState = conditionsValues[index];
    if (!currentConditionState) return [];
    if (
      currentConditionState.selectCustomFieldsValue?.data.metadata.optionType &&
      currentConditionState.selectCustomFieldsValue.data.metadata?.defaultValues
    ) {
      return currentConditionState.selectCustomFieldsValue.data.metadata?.defaultValues?.map(
        (value) => ({
          id: value,
          name: value,
          value,
        })
      );
    }
    return [];
  }, [conditionsValues, index]);

  const handleSelectTypeChange = (option: Option | Option[]) => {
    if (Array.isArray(option)) return;

    setConditionsValues((prev) => {
      const newConditions = _.cloneDeep(prev);
      const newCondition: ConditionState = {
        ...newConditions[index],
        selectTypeValue: option,
        selectBelongValue: undefined,
        selectIdsValues: [],
      };
      newConditions[index] = newCondition;
      return newConditions;
    });
  };

  const handleSelectCustomFieldsChange = (option: Option | Option[]) => {
    if (Array.isArray(option)) return;
    const optionValue = option as Option & { data: Field };
    setConditionsValues((prev) => {
      const newConditions = _.cloneDeep(prev);
      const newCondition: ConditionState = {
        ...newConditions[index],
        selectCustomFieldsValue: optionValue,
        selectBelongValue: undefined,
        selectIdsValues: [],
      };
      newConditions[index] = newCondition;
      return newConditions;
    });
  };

  const handleSelectBelongChange = (option: Option | Option[]) => {
    if (Array.isArray(option)) return;

    setConditionsValues((prev) => {
      const newConditions = _.cloneDeep(prev);
      const newCondition: ConditionState = {
        ...newConditions[index],
        selectBelongValue: option,
      };
      newConditions[index] = newCondition;
      return newConditions;
    });
  };

  const handleSelectIdsChange = (option: Option | Option[]) => {
    if (!Array.isArray(option)) return;

    setConditionsValues((prev) => {
      const newConditions = _.cloneDeep(prev);
      const newCondition: ConditionState = {
        ...newConditions[index],
        selectIdsValues: option,
      };
      newConditions[index] = newCondition;
      return newConditions;
    });
  };

  const handleMultiInputChange = (values: string[]) => {
    setConditionsValues((prev) => {
      const newConditions = _.cloneDeep(prev);
      const type = newConditions[index].selectCustomFieldsValue?.data.type;
      const newCondition: ConditionState = {
        ...newConditions[index],
        selectIdsValues: values.map((value) => ({
          id: value,
          name: value,
          value: type === 'number' ? Number(value) : value,
        })),
      };
      newConditions[index] = newCondition;
      return newConditions;
    });
  };

  const handleOptionsCustomFieldsChange = (option: Option | Option[]) => {
    if (!Array.isArray(option)) return;
    setConditionsValues((prev) => {
      const newConditions = _.cloneDeep(prev);
      const newCondition: ConditionState = {
        ...newConditions[index],
        selectIdsValues: option,
      };
      newConditions[index] = newCondition;
      return newConditions;
    });
  };

  const handleInputNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setConditionsValues((prev) => {
      const newConditions = _.cloneDeep(prev);
      const type = newConditions[index].selectCustomFieldsValue?.data.type;
      const newCondition: ConditionState = {
        ...newConditions[index],
        selectIdsValues: [
          { id: value, name: value, value: type === 'number' ? Number(value) : value },
        ],
      };
      newConditions[index] = newCondition;
      return newConditions;
    });
  };

  return {
    handleSelectTypeChange,
    handleSelectCustomFieldsChange,
    handleSelectBelongChange,
    handleSelectIdsChange,
    handleMultiInputChange,
    handleOptionsCustomFieldsChange,
    handleInputNumberChange,
    selectTypesOptions,
    selectConditionOptions,
    conditionsValues,
    optionsCustomFields,
    genderOptions,
    countryOptions,
  };
};
